import type { Axial, Cube, Point } from './types';

export const HEX_SIZE = 26;

export function cubeToAxial(c: Cube): Axial {
    return { q: c.q, r: c.r };
}

export function axialToCube(a: Axial): Cube {
    return { q: a.q, r: a.r, s: -a.q - a.r };
}

export function axialToPixel(a: Axial): Point {
    const x = HEX_SIZE * Math.sqrt(3) * (a.q + a.r / 2);
    const y = HEX_SIZE * (3 / 2) * a.r;
    return { x, y };
}

export function hexDistance(a: Cube, b: Cube): number {
    return Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.s - b.s));
}

export function pixelToAxial(p: Point): Axial {
    const q = ((Math.sqrt(3) / 3) * p.x - (1 / 3) * p.y) / HEX_SIZE;
    const r = ((2 / 3) * p.y) / HEX_SIZE;
    return { q, r };
}

export function cubeRound(c: Cube): Cube {
    let rq = Math.round(c.q);
    let rr = Math.round(c.r);
    let rs = Math.round(c.s);

    const qDiff = Math.abs(rq - c.q);
    const rDiff = Math.abs(rr - c.r);
    const sDiff = Math.abs(rs - c.s);

    if (qDiff > rDiff && qDiff > sDiff) {
        rq = -rr - rs;
    } else if (rDiff > sDiff) {
        rr = -rq - rs;
    } else {
        rs = -rq - rr;
    }
    return { q: rq, r: rr, s: rs };
}

export function hexVertices(center: Point): Point[] {
    const pts: Point[] = [];
    for (let i = 0; i < 6; i += 1) {
        const angle = (Math.PI / 180) * (60 * i - 30);
        pts.push({
            x: center.x + HEX_SIZE * Math.cos(angle),
            y: center.y + HEX_SIZE * Math.sin(angle),
        });
    }
    return pts;
}

export function pointInPoly(pt: Point, poly: Point[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i += 1) {
        const xi = poly[i].x;
        const yi = poly[i].y;
        const xj = poly[j].x;
        const yj = poly[j].y;
        const isYiAbove = yi > pt.y;
        const isYjAbove = yj > pt.y;
        const isBetweenY = isYiAbove !== isYjAbove;
        const intersects = isBetweenY && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi + 1e-9) + xi;
        if (intersects) inside = !inside;
    }
    return inside;
}
